import { useId, type InputHTMLAttributes, type ReactNode } from 'react';

import { cn } from '../../lib/cn';
import styles from './Input.module.css';

export type InputSize = 'sm' | 'md';

export type InputProps = {
  readonly label?: string;
  readonly error?: string | null;
  readonly hint?: string;
  readonly inputSize?: InputSize;
  readonly leading?: ReactNode;
  readonly trailing?: ReactNode;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'className' | 'size'>;

/** Ошибка выводится текстом под полем (docs/06 §4). */
export function Input({ label, error, hint, inputSize = 'md', leading, trailing, id, ...rest }: InputProps) {
  // Собственный id на каждое поле: раньше несколько `Input` без `name`
  // получали один и тот же `id="input"`, и label ссылался на чужое поле —
  // клик по подписи уводил фокус не туда, а скринридер читал не то имя.
  const generatedId = useId();
  const inputId = id ?? rest.name ?? generatedId;
  const describedBy = error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined;

  return (
    <div className={styles['field']}>
      {label ? (
        <label className={styles['label']} htmlFor={inputId}>
          {label}
        </label>
      ) : null}
      <div className={cn(styles['control'], styles[inputSize], error && styles['invalid'])}>
        {leading ? <span className={styles['adornment']}>{leading}</span> : null}
        <input
          {...rest}
          id={inputId}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={styles['input']}
        />
        {trailing ? <span className={styles['adornment']}>{trailing}</span> : null}
      </div>
      {error ? (
        <p className={styles['error']} id={`${inputId}-error`} role="alert">
          {error}
        </p>
      ) : hint ? (
        <p className={styles['hint']} id={`${inputId}-hint`}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}
